import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { App, Button, Image, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Eye } from 'lucide-react';
import { useEffect, useState } from 'react';
import SearchInput from '../../../components/ui/SearchInput';
import { PLACEHOLDER_IMAGE } from '../../../constants';
import {
    deleteCustomerSuggestedStatus,
    getPendingPrices,
    updateCustomerSuggestedStatus,
    validateCustomerSpecialPrice,
} from '../../../redux/actions/customers';
import { formatCurrency, getProductImageUrl } from '../../../utils';
import AddSuggestedPrice from '../Details/Tabs/SuggestedPrices/Add';
import SalesRepFilter from '../../../components/ui/SalesRepFilter';

const PendingApprovals = () => {
    const { modal: antdModal } = App.useApp();

    const [search, setSearch] = useState('');
    const [user_id, setUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [suggestedPrices, setSuggestedPrices] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        totalRecords: 0,
        totalPages: 0,
    });
    const [showThumbnails, setShowThumbnails] = useState(false);
    const [modal, setModal] = useState({
        isOpen: false,
        data: null,
    });

    const getCustomersPendingPrices = async (body) => {
        setLoading(true);
        const res = await getPendingPrices(body);
        if (res?.ack) {
            setSuggestedPrices(res.suggested_prices);
            setPagination((prev) => ({
                ...prev,
                current: body?.page || 1,
                totalRecords: res.total_count,
                totalPages: Math.ceil(res.total_count / prev.pageSize),
            }));
        } else {
            setSuggestedPrices([]);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                totalRecords: 0,
                totalPages: 0,
            }));
        }
        setLoading(false);
    };

    const handleStatusChange = async (record, status) => {
        const res = await updateCustomerSuggestedStatus({
            id: record.id,
            status,
        });
        if (res?.ack) {
            getCustomersPendingPrices({ page: pagination.current, searchText: search, user_id });
        }
    };

    const handleValidateSpecialPrice = async (record, status) => {
        const res = await validateCustomerSpecialPrice({
            customer_netsuite_id: record.customer_netsuite_id,
            product_code: record.product_code,
        });

        if (res?.ack) {
            const existing = res.special_price?.[0];

            antdModal.confirm({
                centered: true,
                title: 'Override Special Price',
                content: (
                    <div className="p-2">
                        <p className="mb-3">A special price already exists for this item.</p>

                        <div className="mb-3">
                            <strong className="block mb-1">Special Price:</strong>

                            <div className="flex flex-column gap-1">
                                <div className="flex justify-content-between">
                                    <span className="text-600">Price Level:</span>
                                    <span className="font-medium">{existing.price_level}</span>
                                </div>

                                <div className="flex justify-content-between">
                                    <span className="text-600">Price:</span>
                                    <span className="font-medium">{formatCurrency(existing.unit_price)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <strong className="block mb-1">Suggested Price:</strong>

                            <div className="flex flex-column gap-1">
                                <div className="flex justify-content-between">
                                    <span className="text-600">Price Level:</span>
                                    <span className="font-medium">{record.price_level}</span>
                                </div>

                                <div className="flex justify-content-between">
                                    <span className="text-600">Price:</span>
                                    <span className="font-medium">{formatCurrency(record.price)}</span>
                                </div>
                            </div>
                        </div>

                        <p className="mt-3">Do you want to replace the existing special price?</p>
                    </div>
                ),
                okText: 'Proceed',
                onOk: () => {
                    handleStatusChange(record, status);
                },
            });
        } else {
            handleStatusChange(record, status);
        }
    };

    const columns = [
        {
            dataIndex: 'thumbnail',
            key: 'thumbnail',
            align: 'center',
            width: 60,
            hidden: !showThumbnails,
            render: (_, record) => {
                const productImg = getProductImageUrl(record);
                const items = suggestedPrices?.map((item) => getProductImageUrl(item) || PLACEHOLDER_IMAGE);

                return (
                    <Image.PreviewGroup items={items}>
                        <Image
                            src={productImg}
                            alt={record?.product_name}
                            width={50}
                            height={50}
                            fallback={PLACEHOLDER_IMAGE}
                            preview={{
                                mask: <Eye size={16} />,
                            }}
                        />
                    </Image.PreviewGroup>
                );
            },
        },
        {
            title: 'Customer',
            dataIndex: 'customer_code',
            key: 'customer_code',
            render: (_, record) => (
                <span>
                    {record?.customer_code} - {record?.customer_name}
                </span>
            ),
        },
        {
            title: 'Product',
            dataIndex: 'product_code',
            key: 'product_code',
            render: (_, record) => (
                <span>
                    {record?.product_code} - {record?.product_name}
                </span>
            ),
        },
        {
            title: 'Price Level',
            dataIndex: 'price_level',
            key: 'price_level',
            width: 150,
        },
        {
            title: (
                <Typography.Text ellipsis={{ showTooltip: true }} className="max-w-4rem">
                    Requested Price
                </Typography.Text>
            ),
            ellipsis: true,
            dataIndex: 'unit_price',
            key: 'unit_price',
            width: 90,
            render: (text, record) => formatCurrency(text || record?.price),
        },
        {
            title: (
                <Typography.Text ellipsis={{ showTooltip: true }} className="max-w-4rem">
                    Last Purchased Price
                </Typography.Text>
            ),
            ellipsis: true,
            dataIndex: 'last_purchase_price',
            key: 'last_purchase_price',
            render: (text, record) => formatCurrency(text || record?.last_purchase_price),
        },
        {
            title: 'Margin %',
            dataIndex: 'margin_percentage',
            key: 'margin_percentage',
            width: 100,
            render: (_, record) => {
                const sellingPrice = record.price;
                const costPrice = record.last_purchase_price;

                if (!sellingPrice || !costPrice) return <span>-</span>;

                const margin = ((sellingPrice - costPrice) / sellingPrice) * 100;
                const color = margin < 5 ? 'red' : margin < 15 ? 'orange' : 'green';

                return <Tag color={color}>{margin?.toFixed(2)}%</Tag>;
            },
        },
        {
            title: 'Requested By',
            dataIndex: 'created_by',
            key: 'created_by',
            width: 120,
        },
        {
            title: 'Requested At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (text) => text && dayjs(text).format('MM-DD-YYYY HH:mm:ss'),
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            align: 'end',
            render: (_, record) => (
                <Space>
                    <Space>
                        <Tooltip title="Approve">
                            <Button
                                size="small"
                                variant="solid"
                                color="green"
                                icon={<CheckOutlined />}
                                onClick={() => {
                                    antdModal.confirm({
                                        centered: true,
                                        title: 'Approve Suggested Price',
                                        content: 'Are you sure you want to approve this suggested price?',
                                        okText: 'Approve',
                                        okButtonProps: {
                                            color: 'green',
                                            variant: 'solid',
                                            size: 'medium',
                                        },
                                        cancelButtonProps: {
                                            size: 'medium',
                                        },
                                        onOk: () => handleValidateSpecialPrice(record, 1),
                                    });
                                }}
                            >
                                Approve
                            </Button>
                        </Tooltip>

                        <Tooltip title="Reject">
                            <Button
                                size="small"
                                variant="solid"
                                color="red"
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    antdModal.confirm({
                                        centered: true,
                                        title: 'Reject Suggested Price',
                                        content: 'Are you sure you want to reject this suggested price?',
                                        okText: 'Reject',
                                        okButtonProps: {
                                            color: 'red',
                                            variant: 'solid',
                                            size: 'medium',
                                        },
                                        cancelButtonProps: {
                                            size: 'medium',
                                        },
                                        onOk: () => handleStatusChange(record, 2),
                                    });
                                }}
                            >
                                Reject
                            </Button>
                        </Tooltip>
                    </Space>
                    <Space>
                        <Tooltip title="Delete">
                            <Button
                                size="small"
                                variant="solid"
                                color="danger"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                    antdModal.confirm({
                                        centered: true,
                                        title: 'Delete Suggested Price',
                                        content:
                                            'This action cannot be undone. Are you sure you want to delete this suggested price?',
                                        okText: 'Delete',
                                        okButtonProps: {
                                            color: 'red',
                                            variant: 'solid',
                                            size: 'medium',
                                        },
                                        cancelButtonProps: {
                                            variant: 'outlined',
                                            color: 'danger',
                                            size: 'medium',
                                        },
                                        onOk: async () => {
                                            const res = await deleteCustomerSuggestedStatus({ id: record.id });
                                            if (res?.ack) {
                                                getCustomersPendingPrices({
                                                    page: pagination.current,
                                                    searchText: search,
                                                    user_id,
                                                });
                                            }
                                        },
                                    });
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Edit">
                            <Button
                                size="small"
                                variant="solid"
                                color="primary"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setModal({ isOpen: true, data: record });
                                }}
                            />
                        </Tooltip>
                    </Space>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        getCustomersPendingPrices({ page: 1 });
    }, []);

    return (
        <div className="pending-approvals-page">
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className="page-title">
                        Pending Approvals
                    </Typography.Title>
                </div>

                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                    <div className="flex align-items-center gap-2 flex-wrap flex-1">
                        <SearchInput
                            className="lg:max-w-20rem w-full"
                            onSearch={(searchText) => {
                                setSearch(searchText);
                                getCustomersPendingPrices({ searchText, page: 1, user_id });
                            }}
                        />
                        <SalesRepFilter
                            placeholder="Search Requested By"
                            value={user_id}
                            onChange={(user_id) => {
                                setUserId(user_id);
                                getCustomersPendingPrices({ user_id, searchText: search, page: 1 });
                            }}
                        />
                    </div>
                    <Space align="center" size={4}>
                        <Typography.Text className="form-item-label">Show Thumbnails</Typography.Text>
                        <Switch
                            checked={showThumbnails}
                            onChange={() => setShowThumbnails((prev) => !prev)}
                            disabled={!suggestedPrices?.length}
                        />
                    </Space>
                </div>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id || record?.code}
                dataSource={suggestedPrices}
                scroll={{ x: 'max-content', y: 720 }}
                loading={loading}
                components={{
                    body: {
                        cell: ({ className, ...rest }) => (
                            <td
                                {...rest}
                                className={
                                    suggestedPrices?.length && clsx(className, showThumbnails ? 'py-0' : 'py-1')
                                }
                            />
                        ),
                    },
                }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.totalRecords,
                    showSizeChanger: false,
                    responsive: true,
                    size: 'small',
                    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
                    onChange: (page) => getCustomersPendingPrices({ page, searchText: search, user_id }),
                }}
            />

            {modal.isOpen && (
                <AddSuggestedPrice
                    open={modal.isOpen}
                    onClose={() => {
                        setModal({ isOpen: false, data: null });
                    }}
                    data={modal.data}
                    customer={{
                        code: modal.data?.customer_code,
                        title: modal.data?.customer_name,
                        customer_netsuite_id: modal.data?.customer_netsuite_id,
                    }}
                    afterDataUpdate={() => {
                        getCustomersPendingPrices({ page: pagination.current, searchText: search, user_id });
                    }}
                />
            )}
        </div>
    );
};

export default PendingApprovals;
